import React, { useState, useMemo, useEffect } from "react";
import { Header } from "../../components/Header/Header";
import { Navigator } from "../../components/Navigator/Navigator";
import { ChevronDown, ChevronUp, AlertCircle, FileText } from 'lucide-react';
import axios from "axios";

function LanguageCheck() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const api = process.env.REACT_APP_API_URL;

  // Cargar datos del API al montar el componente
  useEffect(() => {
    cargarErrores();
  }, []);

  // Cargar datos del API
  const cargarErrores = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${api}/development/language-check`);
      const result = await response.data;
      setData(result.message);
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  // Agrupar errores por desarrollo
  const desarrollosConErrores = useMemo(() => {
    if (!data?.errores) return [];

    const agrupados = {};
    
    data.errores.forEach(item => {
      if (!agrupados[item.id]) {
        agrupados[item.id] = {
          id: item.id,
          errores: []
        };
      }
      agrupados[item.id].errores.push(item);
    });

    return Object.values(agrupados).map(dev => {
      const totalErrores = dev.errores.reduce((sum, e) => sum + e.errores.length, 0);
      const nombre = dev.errores[0].textoOriginal.split('\n')[0];
      
      return {
        ...dev,
        totalErrores,
        nombre: nombre.length > 50 ? nombre.substring(0, 50) + '...' : nombre,
        camposAfectados: [...new Set(dev.errores.map(e => e.campo))]
      };
    }).sort((a, b) => b.totalErrores - a.totalErrores);
  }, [data]);

  // Paginación
  const totalPages = Math.ceil(desarrollosConErrores.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const desarrollosPaginados = desarrollosConErrores.slice(startIndex, startIndex + itemsPerPage);

  const toggleExpanded = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <div>
      <main className="w-[100vw] max-w-[100vw] bg-[#eaeaea]">
        <Header />
        <div className="flex h-full w-full pr-[20px] pb-[20px] ">
          <Navigator />
          <div className="flex w-full gap-[10px]">
            {/* Contenido principal */}
            <div className="w-full bg-white rounded-lg shadow-md p-6 overflow-y-auto max-h-[calc(100vh-100px)]">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Cargando errores...</p>
                  </div>
                </div>
              ) : !data ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">
                      Error al cargar los datos
                    </h2>
                    <button
                      onClick={cargarErrores}
                      className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition"
                    >
                      Reintentar
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Header con estadísticas */}
                  <div className="mb-6 pb-6 border-b border-gray-200">
                    <h1 className="text-3xl font-bold text-gray-800 mb-4">
                      Errores de Ortografía en Desarrollos
                    </h1>
                    <div className="flex gap-6 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">Total Revisados:</span>
                        <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-semibold">
                          {data.totalRevisados}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">Con Errores:</span>
                        <span className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full font-semibold">
                          {data.totalConErrores}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">Total Errores:</span>
                        <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full font-semibold">
                          {desarrollosConErrores.reduce((sum, d) => sum + d.totalErrores, 0)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Lista de Desarrollos */}
                  <div className="space-y-4 mb-6">
                    {desarrollosPaginados.map((desarrollo) => (
                      <div
                        key={desarrollo.id}
                        className="bg-gray-50 rounded-lg overflow-hidden border-l-4 border-red-500"
                      >
                        {/* Cabecera del desarrollo */}
                        <div
                          onClick={() => toggleExpanded(desarrollo.id)}
                          className="p-4 cursor-pointer hover:bg-gray-100 transition"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <span className="bg-gray-200 text-gray-700 px-3 py-1 rounded-full text-sm font-semibold">
                                  ID: {desarrollo.id}
                                </span>
                                <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                                  <AlertCircle size={16} />
                                  {desarrollo.totalErrores} {desarrollo.totalErrores === 1 ? 'error' : 'errores'}
                                </span>
                              </div>
                              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                                {desarrollo.nombre}
                              </h3>
                              <div className="flex gap-2 flex-wrap">
                                {desarrollo.camposAfectados.map(campo => (
                                  <span
                                    key={campo}
                                    className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs"
                                  >
                                    {campo}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <div className="text-gray-400">
                              {expandedId === desarrollo.id ? (
                                <ChevronUp size={24} />
                              ) : (
                                <ChevronDown size={24} />
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Detalles expandidos */}
                        {expandedId === desarrollo.id && (
                          <div className="border-t border-gray-200 p-4 bg-white">
                            {desarrollo.errores.map((campo, idx) => (
                              <div key={idx} className="mb-6 last:mb-0">
                                <div className="flex items-center gap-2 mb-3">
                                  <FileText size={18} className="text-gray-600" />
                                  <h4 className="font-semibold text-gray-700 capitalize">
                                    Campo: {campo.campo}
                                  </h4>
                                </div>

                                <div className="bg-gray-50 rounded-lg p-4 mb-3">
                                  <p className="text-sm text-gray-600 mb-2 font-medium">Texto Original:</p>
                                  <p className="text-sm text-gray-800 whitespace-pre-wrap border-l-2 border-gray-300 pl-3">
                                    {campo.textoOriginal}
                                  </p>
                                </div>

                                <div className="space-y-3">
                                  {campo.errores.map((error, errorIdx) => (
                                    <div
                                      key={errorIdx}
                                      className="bg-gray-50 rounded-lg p-4 border border-red-200"
                                    >
                                      <div className="flex items-start gap-3 mb-2">
                                        <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-medium">
                                          {error.tipo}
                                        </span>
                                      </div>
                                      <p className="text-sm text-gray-700 mb-3">{error.mensaje}</p>
                                      
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                          <p className="text-xs font-semibold text-gray-600 mb-1">
                                            Palabra con error:
                                          </p>
                                          <span className="inline-block bg-red-50 text-red-800 px-3 py-1 rounded font-mono text-sm">
                                            {error.palabraError}
                                          </span>
                                        </div>
                                        
                                        {error.sugerencias.length > 0 && (
                                          <div>
                                            <p className="text-xs font-semibold text-gray-600 mb-1">
                                              Sugerencias:
                                            </p>
                                            <div className="flex flex-wrap gap-2">
                                              {error.sugerencias.map((sug, sugIdx) => (
                                                <span
                                                  key={sugIdx}
                                                  className="bg-green-50 text-green-700 px-2 py-1 rounded text-xs"
                                                >
                                                  {sug}
                                                </span>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Paginación */}
                  {totalPages > 1 && (
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center justify-between">
                        <button
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                        >
                          Anterior
                        </button>
                        
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">
                            Página {currentPage} de {totalPages}
                          </span>
                          <span className="text-xs text-gray-500">
                            ({startIndex + 1}-{Math.min(startIndex + itemsPerPage, desarrollosConErrores.length)} de {desarrollosConErrores.length})
                          </span>
                        </div>

                        <button
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                        >
                          Siguiente
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>    
        </div>
      </main>
    </div>
  );
}

export { LanguageCheck };